import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../Modal/Modal';
import { Button } from '../Button/Button';
import { Input } from '../Input/Input';
import { csvExportApi } from '../../../api/csvExport';
import { formatShortDate } from '../../../utils/dateFormatter';
import type { ReviewData, CriterionData } from '../../../types';

interface CSVExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  products: ReviewData[];
  criteria: CriterionData[];
  getEvaluationPosition: (productIndex: number, criterionIndex: number) => { x: number; y: number } | null;
  getGrades: (criterionIndex: number) => any[];
  getRubricData: () => any;
}

const PLACEHOLDER_OPTIONS = [
  { value: '{product_name}', label: 'product_name' },
  { value: '{criterion_name}', label: 'criterion_name' },
  { value: '{grade}', label: 'grade' },
  { value: '{score}', label: 'score' },
  { value: '{comment}', label: 'comment' },
  { value: '{evaluation_date}', label: 'evaluation_date' },
  { value: '{evaluator}', label: 'evaluator' },
  { value: '{overall_score}', label: 'overall_score' },
  { value: '{rank}', label: 'rank' },
  { value: '{category}', label: 'category' }
];

export const CSVExportModal: React.FC<CSVExportModalProps> = ({
  isOpen,
  onClose,
  products,
  criteria,
  getEvaluationPosition,
  getGrades,
  getRubricData
}) => {
  const { t } = useTranslation();
  const [csvTemplate, setCsvTemplate] = useState('');
  const [filename, setFilename] = useState('evaluation_export');
  const [exportFormat, setExportFormat] = useState<'csv' | 'tsv'>('csv');
  const [includeHeader, setIncludeHeader] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [exportStatus, setExportStatus] = useState<{
    can_export: boolean;
    current_count: number;
    max_count: number | null;
    remaining: number | null;
  } | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadExportStatus();
      // Set default template
      setCsvTemplate('{product_name},{criterion_name},{grade},{score},{comment}');
    }
  }, [isOpen]);

  const loadExportStatus = async () => {
    try {
      const status = await csvExportApi.getExportStatus();
      setExportStatus(status);
    } catch (error) {
      console.error('Failed to load CSV export status:', error);
    }
  };

  const handlePlaceholderClick = (placeholder: string) => {
    const textarea = document.getElementById('csv-template') as HTMLTextAreaElement;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newValue = csvTemplate.slice(0, start) + placeholder + csvTemplate.slice(end);
      setCsvTemplate(newValue);
      
      // Set cursor position after inserted placeholder
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + placeholder.length, start + placeholder.length);
      }, 0);
    }
  };

  const handleExport = async () => {
    if (!csvTemplate.trim()) {
      alert(t('csvExport.templateRequired'));
      return;
    }

    setIsExporting(true);
    try {
      const exportData = {
        template: csvTemplate,
        format: exportFormat,
        includeHeader,
        filename
      };

      const response = await csvExportApi.exportCSV(exportData);
      
      // Create download link
      const blob = new Blob([response.data], { 
        type: exportFormat === 'csv' ? 'text/csv' : 'text/tab-separated-values' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${filename}.${exportFormat}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Update export status
      await loadExportStatus();
      onClose();
    } catch (error) {
      console.error('CSV export failed:', error);
      alert(t('csvExport.exportFailed'));
    } finally {
      setIsExporting(false);
    }
  };

  const previewData = () => {
    if (!csvTemplate.trim() || products.length === 0) return '';
    
    // Generate preview with first product and first criterion
    const firstProduct = products[0];
    const firstCriterion = criteria[0];
    const grades = getGrades(0);
    const rubricData = getRubricData();
    
    let preview = csvTemplate;
    preview = preview.replace(/{product_name}/g, firstProduct.reviewTitle);
    preview = preview.replace(/{criterion_name}/g, firstCriterion?.title || '');
    preview = preview.replace(/{grade}/g, 'A');
    preview = preview.replace(/{score}/g, '85');
    preview = preview.replace(/{comment}/g, t('csvExport.sampleComment'));
    preview = preview.replace(/{evaluation_date}/g, formatShortDate(new Date()));
    preview = preview.replace(/{evaluator}/g, t('csvExport.currentUser'));
    preview = preview.replace(/{overall_score}/g, '82.5');
    preview = preview.replace(/{rank}/g, '1');
    preview = preview.replace(/{category}/g, firstCriterion?.title || '');
    
    return preview;
  };

  if (!exportStatus) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">
            {t('csvExport.title')}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            ×
          </button>
        </div>

        {/* Export Status */}
        <div className="mb-6 p-4 bg-blue-50 rounded-lg">
          <div className="flex items-center justify-between">
            <span className="text-sm text-blue-700">
              {t('csvExport.exportStatus')}
            </span>
            <span className="text-sm font-medium text-blue-900">
              {exportStatus.remaining !== null 
                ? t('csvExport.remaining', { count: exportStatus.remaining })
                : t('csvExport.unlimited')
              }
            </span>
          </div>
        </div>

        {/* Template Editor */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('csvExport.templateLabel')}
          </label>
          <textarea
            id="csv-template"
            value={csvTemplate}
            onChange={(e) => setCsvTemplate(e.target.value)}
            className="w-full h-32 px-3 py-2 border border-gray-300 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder={t('csvExport.templatePlaceholder')}
          />
          <p className="mt-1 text-xs text-gray-500">
            {t('csvExport.templateHelp')}
          </p>
        </div>

        {/* Placeholder Buttons */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('csvExport.availablePlaceholders')}
          </label>
          <div className="flex flex-wrap gap-2">
            {PLACEHOLDER_OPTIONS.map((option) => (
              <button
                key={option.value}
                onClick={() => handlePlaceholderClick(option.value)}
                className="px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
              >
                {option.value}
              </button>
            ))}
          </div>
        </div>

        {/* Preview */}
        {csvTemplate && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('csvExport.preview')}
            </label>
            <div className="p-3 bg-gray-50 rounded-md text-sm font-mono text-gray-700">
              {previewData()}
            </div>
          </div>
        )}

        {/* Export Options */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('csvExport.filename')}
            </label>
            <Input
              value={filename}
              onChange={(e) => setFilename(e.target.value)}
              placeholder="evaluation_export"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('csvExport.format')}
            </label>
            <select
              value={exportFormat}
              onChange={(e) => setExportFormat(e.target.value as 'csv' | 'tsv')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="csv">CSV</option>
              <option value="tsv">TSV</option>
            </select>
          </div>
          <div className="flex items-center pt-6">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={includeHeader}
                onChange={(e) => setIncludeHeader(e.target.checked)}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">
                {t('csvExport.includeHeader')}
              </span>
            </label>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={isExporting}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleExport}
            disabled={!exportStatus.can_export || isExporting || !csvTemplate.trim()}
          >
            {isExporting ? t('csvExport.exporting') : t('csvExport.export')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};